import type { SvgIconInput, SvgIconTheme } from '@publishing/components/ui/Icon';
import {
  ActionManager,
  EtcLeadform,
  EtcFloatingCall,
  ActionSpot,
} from '@publishing/components/ui/SvgSprite';

/** 검증·마스킹·autoComplete 등 내부 동작용 (DOM `<input type>`은 `htmlTypeMap[inputType]` 또는 `Input`의 `htmlType` prop) */
export type InputType = 'text' | 'email' | 'number' | 'tel' | 'date' | 'yearMonth';

/**
 * 필드 프리셋 키. `<Input type="company" />`처럼 `type`으로 전달합니다.
 * `type="email"`은 이메일 프리셋(라벨·아이콘·name 등)으로만 해석합니다.
 */
export type InputPreset =
  | 'company'
  | 'phone'
  | 'officeArea'
  | 'userCount'
  | 'email'
  | 'brokerCompany'
  | 'preferredRegion'
  | 'periodMovein'
  | 'periodUse'
  | 'needsEtc'
  | 'partnerName';

/** @deprecated `InputPreset`을 사용하세요. */
export type InputVariant = InputPreset;

export interface VariantPreset {
  /** 동작 타입 (공개 prop `type`과 이름만 겹칠 뿐, 다른 개념입니다) */
  inputType: InputType;
  label: string;
  name?: string;
  icon?: SvgIconInput;
  iconTheme?: SvgIconTheme;
  required?: boolean;
  comma?: boolean;
  max?: number;
  helperMessage?: string;
}

/*
company	            : company + name
phone               : Phone
email               : Email
userCount           : 예상사용인원
isPrivacy           : 개인정보수집 및 활용 동의
isMarketing         : 마케팅 선택 동의
officeArea          : 공사 필요 면적
productType         : 상품 구분
interestedBranchIds : 관심 지점
downloadPurpose     : 다운로드 목적
preferredRegion     : 지역 - 선호 지역(마케팅)
importantFactor     : 중요한요소
expectedSpace       : 기대되는 공간
remark              : 특이사항
trialCount          : 사용 인원
brokerCompany       : 중개사_고객사명
periodMovein        : 희망 입주 시기
periodUse           : 예상사용기간
needsEtc	          : 기타니즈(TEXT)
needs	              : 주요니즈(TEXT)
moveinFlexible      : 입주시기 조정 여부(TEXT)

*/
export const VARIANT_PRESETS: Record<InputPreset, VariantPreset> = {
  company: { inputType: 'text', label: '회사명 또는 성함', name: 'companyName', icon: EtcLeadform, iconTheme: 'name', required: true },
  phone:   { inputType: 'tel', label: '휴대전화 번호', name: 'phone', icon: EtcFloatingCall, required: true },
  email:   { inputType: 'email', label: '이메일 주소', name: 'email', icon: EtcLeadform, iconTheme: 'mail', required: true },
  userCount: { inputType: 'number', label: '예상 이용 인원', name: 'userCount', icon: EtcLeadform, iconTheme: 'people', required: true, max: 999 },
  officeArea: { inputType: 'number', label: '희망 면적(평)', name: 'officeArea', icon: EtcLeadform, iconTheme: 'area', required: true, comma: true },

  brokerCompany: { inputType: 'text', label: '고객사명', name: 'brokerCompany', icon: EtcLeadform, iconTheme: 'name', required: true },
  preferredRegion: { inputType: 'text', label: '선호 지역', name: 'preferredRegion', icon: ActionSpot, required: true },
  periodMovein: { inputType: 'text', label: '입주 희망 시기', name: 'periodMovein', required: true },
  periodUse: { inputType: 'text', label: '예) 6개월/1년/1년이상', name: 'periodUse', required: true },
  needsEtc: { inputType: 'text', label: '', name: 'needsEtc',  },
  /** 패파트너 이름 — SF `remark` 필드로 제출 */
  partnerName: { inputType: 'text', label: '패파트너 이름', name: 'remark', icon: ActionManager, required: true,  },
};
// 셀포랑 연동되는 부분이 Name이 필요한지.
// 셀포랑 일괄적으로 매핑해서 연동할 수 있는 파일이 필요함. 그게 이파일이었으면 좋을 것 같음 !!


/** `type` prop이 프리셋 키인지 판별 */
export function isInputPreset(value: string | undefined): value is InputPreset {
  return value != null && Object.prototype.hasOwnProperty.call(VARIANT_PRESETS, value);
}

/** 공개 `type` prop: 프리셋 또는 프리셋이 아닌 동작 타입(`email` 제외 — `type="email"`은 항상 프리셋) */
export type InputPropType = InputPreset | Exclude<InputType, 'email'>;
